import type { MusclePriorities, VolumeTargets } from '@cadencia/contracts';
import { MUSCLES, type Muscle } from '../catalog/muscles.js';
import {
  estimateMinutes,
  weeklyVolumeByMuscle,
  type ResolvedExercise,
  type ResolvedSession,
} from './block-builder.js';
import { priorityFor } from './priorities.js';

/**
 * Ajuste de las series prescritas para acercarse a un OBJETIVO de volumen por
 * musculo (docs/TRAINING_ENGINE.md §4.3).
 *
 * Es la pieza que faltaba para que una propuesta de volumen sirva de algo. El
 * motor decia «pectoral: 14 → 16 series» y esa frase no llegaba a ninguna serie
 * prescrita; esto la convierte en series concretas repartidas por las sesiones
 * de la semana.
 *
 * Funcion PURA sobre las sesiones ya resueltas: no sabe de fases, de splits ni
 * de la base de datos. Recibe la semana construida y devuelve otra.
 */

/**
 * Series efectivas semanales que se quieren para cada musculo.
 *
 * El tipo se define en el contrato porque se PERSISTE en la columna
 * `volume_targets`. Se reexporta aqui, igual que `MusclePriorities`, para que
 * el dominio siga siendo el sitio natural donde buscarlo. Dos definiciones del
 * mismo mapa acabarian divergiendo.
 */
export type { VolumeTargets };

/**
 * Diferencia por debajo de la cual no merece la pena tocar nada.
 *
 * Una serie es la unidad minima que se puede prescribir, asi que perseguir
 * medias series solo produce cambios que el usuario no distingue y ruido en el
 * historial.
 */
export const VOLUME_FIT_TOLERANCE = 1;

/** Series minimas de un ejercicio que se queda en la sesion. */
export const MIN_SETS_PER_EXERCISE = 1;

/** Series maximas por ejercicio, para no concentrar la semana en un hueco. */
export const MAX_SETS_PER_EXERCISE = 6;

export interface VolumeFitInput {
  /** Sesiones de UNA semana, ya resueltas contra el equipamiento. */
  sessions: readonly ResolvedSession[];
  /**
   * `| undefined` a proposito: una fila guardada ANTES de que existiera la
   * columna `volume_targets` no tiene el campo, y un `ALTER TABLE` con valor por
   * defecto no cambia `sync_seq`, asi que esa fila no se vuelve a bajar nunca.
   * La copia local se queda sin el campo para siempre.
   */
  targets: VolumeTargets | undefined;
  maxSessionMinutes: number;
  musclePriorities?: MusclePriorities | undefined;
}

export interface VolumeFitChange {
  muscle: Muscle;
  from: number;
  to: number;
  target: number;
  /** No se alcanzo el objetivo, y por que. */
  shortfallReason: string | null;
}

export interface VolumeFitResult {
  sessions: ResolvedSession[];
  changes: VolumeFitChange[];
  reasonCodes: string[];
}

const ORDEN_DE_PRIORIDAD: Record<string, number> = { high: 0, normal: 1, low: 2 };

/**
 * Orden en que se atienden los musculos.
 *
 * Primero los prioritarios (§4.4) y, a igualdad, el que mas lejos esta de su
 * objetivo. Importa porque los huecos de tiempo son limitados: el primero que
 * pasa se lleva el margen que queda en la sesion, y quien elige ese orden esta
 * eligiendo a quien se beneficia cuando no cabe todo.
 */
function ordenarMusculos(
  targets: VolumeTargets,
  actual: ReadonlyMap<string, number>,
  priorities: MusclePriorities,
): Muscle[] {
  return MUSCLES.filter((muscle) => targets[muscle] !== undefined).sort((a, b) => {
    const prioridad =
      (ORDEN_DE_PRIORIDAD[priorityFor(a, priorities)] ?? 1) -
      (ORDEN_DE_PRIORIDAD[priorityFor(b, priorities)] ?? 1);
    if (prioridad !== 0) return prioridad;

    const faltaA = Math.abs((targets[a] as number) - (actual.get(a) ?? 0));
    const faltaB = Math.abs((targets[b] as number) - (actual.get(b) ?? 0));
    return faltaB - faltaA;
  });
}

/** Ejercicios de la semana cuyo musculo PRIMARIO es este. */
function ejerciciosDe(
  sessions: readonly ResolvedSession[],
  muscle: Muscle,
): { sesion: number; ejercicio: number }[] {
  const salida: { sesion: number; ejercicio: number }[] = [];
  sessions.forEach((sesion, indiceSesion) => {
    sesion.exercises.forEach((item, indiceEjercicio) => {
      if (item.exercise.primaryMuscles.includes(muscle)) {
        salida.push({ sesion: indiceSesion, ejercicio: indiceEjercicio });
      }
    });
  });
  return salida;
}

function clonar(sessions: readonly ResolvedSession[]): ResolvedSession[] {
  return sessions.map((sesion) => ({
    ...sesion,
    exercises: sesion.exercises.map((item) => ({ ...item })),
  }));
}

/**
 * Acerca el volumen semanal a los objetivos declarados.
 *
 * Solo toca ejercicios cuyo musculo PRIMARIO es el que se ajusta. Tocar los
 * secundarios permitiria afinar mas, pero cambiaria el estimulo de ejercicios
 * que estan ahi por otro motivo: subir dos series de press de banca para llegar
 * al objetivo de triceps convierte una sesion de pecho en otra cosa.
 *
 * El precio de esa decision es que los secundarios SE MUEVEN solos, y no se
 * corrigen: perseguirlos entraria en un ciclo —subir pectoral sube triceps,
 * bajar triceps baja pectoral— que no converge. Se ajusta cada musculo una vez,
 * en orden, y lo que quede se informa en `changes`.
 */
export function fitWeeklyVolume(input: VolumeFitInput): VolumeFitResult {
  const sessions = clonar(input.sessions);
  const priorities = input.musclePriorities ?? {};
  // Sin objetivos no se ajusta nada, que es distinto de reventar. Una funcion
  // pura no puede tumbar la pantalla de entrenar por un campo que falta.
  const targets = input.targets ?? {};
  const reasonCodes: string[] = [];
  const changes: VolumeFitChange[] = [];

  const inicial = weeklyVolumeByMuscle(sessions);
  const orden = ordenarMusculos(targets, inicial, priorities);

  if (orden.length === 0) {
    return { sessions, changes, reasonCodes: ['no_targets_declared'] };
  }

  for (const muscle of orden) {
    const objetivo = targets[muscle] as number;
    const desde = weeklyVolumeByMuscle(sessions).get(muscle) ?? 0;

    if (Math.abs(objetivo - desde) < VOLUME_FIT_TOLERANCE) {
      continue;
    }

    const motivo = ajustarMusculo(sessions, muscle, objetivo, input.maxSessionMinutes);
    const hasta = weeklyVolumeByMuscle(sessions).get(muscle) ?? 0;

    if (hasta !== desde || motivo !== null) {
      changes.push({ muscle, from: desde, to: hasta, target: objetivo, shortfallReason: motivo });
    }
  }

  for (const sesion of sessions) {
    sesion.estimatedMinutes = estimateMinutes(sesion.exercises);
  }

  if (changes.length === 0) reasonCodes.push('already_on_target');
  if (changes.some((cambio) => cambio.shortfallReason !== null)) {
    reasonCodes.push('target_not_fully_reached');
  }

  return { sessions, changes, reasonCodes };
}

/**
 * Mueve las series de un musculo hasta el objetivo, de una en una.
 *
 * De una en una y repartiendo entre los ejercicios disponibles, en lugar de
 * cargarlo todo en el primero: cuatro series extra en un solo ejercicio no dan
 * el mismo estimulo que dos en cada uno de dos, y ademas revientan el limite de
 * tiempo de esa sesion.
 *
 * Devuelve `null` si llego al objetivo, o el motivo por el que se quedo corto.
 */
function ajustarMusculo(
  sessions: ResolvedSession[],
  muscle: Muscle,
  objetivo: number,
  maxSessionMinutes: number,
): string | null {
  const huecos = ejerciciosDe(sessions, muscle);
  if (huecos.length === 0) return 'no_exercise_for_muscle';

  // Cota de seguridad del bucle: cada vuelta mueve una serie, y ni el numero de
  // huecos ni el maximo por ejercicio son grandes. Sin ella, un objetivo
  // inalcanzable podria dar vueltas indefinidamente.
  const maximoDeVueltas = huecos.length * MAX_SETS_PER_EXERCISE + 1;

  for (let vuelta = 0; vuelta < maximoDeVueltas; vuelta += 1) {
    const actual = weeklyVolumeByMuscle(sessions).get(muscle) ?? 0;
    const falta = objetivo - actual;
    if (Math.abs(falta) < VOLUME_FIT_TOLERANCE) return null;

    const sube = falta > 0;
    const hueco = siguienteHueco(sessions, huecos, sube, maxSessionMinutes);
    if (hueco === null) return sube ? 'no_room_left' : 'minimum_sets_reached';

    const item = sessions[hueco.sesion]?.exercises[hueco.ejercicio] as ResolvedExercise;
    item.sets += sube ? 1 : -1;
  }

  return 'iteration_limit';
}

/**
 * Elige el ejercicio al que darle (o quitarle) la siguiente serie.
 *
 * Al subir se busca el que menos series tiene: reparte. Al bajar, el que mas:
 * recorta primero donde sobra.
 */
function siguienteHueco(
  sessions: readonly ResolvedSession[],
  huecos: readonly { sesion: number; ejercicio: number }[],
  sube: boolean,
  maxSessionMinutes: number,
): { sesion: number; ejercicio: number } | null {
  let mejor: { sesion: number; ejercicio: number } | null = null;
  let mejorSeries = sube ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;

  for (const hueco of huecos) {
    const sesion = sessions[hueco.sesion] as ResolvedSession;
    const item = sesion.exercises[hueco.ejercicio] as ResolvedExercise;

    if (sube) {
      if (item.sets >= MAX_SETS_PER_EXERCISE) continue;
      // El limite de tiempo de la sesion es un limite duro del usuario, no una
      // preferencia: una sesion de 100 minutos no se hace, se abandona.
      const conUnaMas = sesion.exercises.map((otro) =>
        otro === item ? { ...otro, sets: otro.sets + 1 } : otro,
      );
      if (estimateMinutes(conUnaMas) > maxSessionMinutes) continue;
      if (item.sets < mejorSeries) {
        mejor = hueco;
        mejorSeries = item.sets;
      }
    } else {
      if (item.sets <= MIN_SETS_PER_EXERCISE) continue;
      if (item.sets > mejorSeries) {
        mejor = hueco;
        mejorSeries = item.sets;
      }
    }
  }

  return mejor;
}
